import { Html5QrcodeScanner, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { useEffect, useRef, useState } from "react";
import ButtonBase from "react-bootstrap/Button";
import { X, Pause, Upc } from "react-bootstrap-icons";
import Styled from "styled-components";

const qrcodeRegionId = "html5qr-code-full-region";
const qrCodeRegionDashboardId = "html5qr-code-full-region__dashboard";
const qrCodeStartButton = "html5-qrcode-button-camera-start";
const qrCodeStopButton = "html5-qrcode-button-camera-stop";

const Button = Styled(ButtonBase)`
    margin: 5px;
`;

const BarcodeScanner = Styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 50vw;
    border: none !important;

    @media only screen and (orientation: portrait) and (-webkit-min-device-pixel-ratio: 1.5) {
        width: 100vw;
    }    

    & #html5-qrcode-button-camera-start, & #html5-qrcode-button-camera-stop {
        visibility: hidden;
    } 

    & .form-select {
        --bs-body-color: #dee2e6;
        --bs-body-color-rgb: 222, 226, 230;
        --bs-body-bg: #212529;
        --bs-body-bg-rgb: 33, 37, 41;
        --bs-emphasis-color: #fff;
        --bs-emphasis-color-rgb: 255, 255, 255;
        --bs-secondary-color: #dee2e6bf;
        --bs-secondary-color-rgb: 222, 226, 230;
        --bs-secondary-bg: #343a40;
        --bs-secondary-bg-rgb: 52, 58, 64;
        --bs-tertiary-color: #dee2e680;
        --bs-tertiary-color-rgb: 222, 226, 230;
        --bs-tertiary-bg: #2b3035;
        --bs-tertiary-bg-rgb: 43, 48, 53;
        --bs-primary-text-emphasis: #6ea8fe;
        --bs-secondary-text-emphasis: #a7acb1;
        --bs-success-text-emphasis: #75b798;
        --bs-info-text-emphasis: #6edff6;
        --bs-warning-text-emphasis: #ffda6a;
        --bs-danger-text-emphasis: #ea868f;
        --bs-light-text-emphasis: #f8f9fa;
        --bs-dark-text-emphasis: #dee2e6;
        --bs-primary-bg-subtle: #031633;
        --bs-secondary-bg-subtle: #161719;
        --bs-success-bg-subtle: #051b11;
        --bs-info-bg-subtle: #032830;
        --bs-warning-bg-subtle: #332701;
        --bs-danger-bg-subtle: #2c0b0e;
        --bs-light-bg-subtle: #343a40;
        --bs-dark-bg-subtle: #1a1d20;
        --bs-primary-border-subtle: #084298;
        --bs-secondary-border-subtle: #41464b;
        --bs-success-border-subtle: #0f5132;
        --bs-info-border-subtle: #087990;
        --bs-warning-border-subtle: #997404;
        --bs-danger-border-subtle: #842029;
        --bs-light-border-subtle: #495057;
        --bs-dark-border-subtle: #343a40;
        --bs-heading-color: inherit;
        --bs-link-color: #6ea8fe;
        --bs-link-hover-color: #8bb9fe;
        --bs-link-color-rgb: 110, 168, 254;
        --bs-link-hover-color-rgb: 139, 185, 254;
        --bs-code-color: #e685b5;
        --bs-highlight-color: #dee2e6;
        --bs-highlight-bg: #664d03;
        --bs-border-color: #495057;
        --bs-border-color-translucent: #ffffff26;
        --bs-form-valid-color: #75b798;
        --bs-form-valid-border-color: #75b798;
        --bs-form-invalid-color: #ea868f;
        --bs-form-invalid-border-color: #ea868f;
    
        --bs-form-select-bg-img: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='%23dee2e6' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3E%3C/svg%3E");
        appearance: none;
        background-color: var(--bs-body-bg);
        background-image: var(--bs-form-select-bg-img), var(--bs-form-select-bg-icon, none);
        background-position: right 0.75rem center;
        background-repeat: no-repeat;
        background-size: 16px 12px;
        border: var(--bs-border-width) solid var(--bs-border-color);
        border-radius: var(--bs-border-radius);
        display: block;
        font-size: 1rem;
        font-weight: 400;
        padding: 0.375rem 2.25rem 0.375rem 0.75rem;
        width: 100%;
        
        color: var(--bs-body-color);
    }
`;

// Creates the configuration object for Html5QrcodeScanner.
const createConfig = (props) => {
  let config = {
    focusMode: "continuous",
    advanced: [{ zoom: 2.0 }],
    rememberLastUsedCamera: true,
    experimentalFeatures: {
      useBarCodeDetectorIfSupported: true,
    },
    willReadFrequently: true,
    // supportedScanTypes: [
    //   // Html5QrcodeScanType.SCAN_TYPE_FILE,
    //   Html5QrcodeScanType.SCAN_TYPE_CAMERA,
    // ],
  };

  if (props.fps) {
    config.fps = props.fps;
  }
  if (props.qrbox) {
    config.qrbox = props.qrbox;
  }
  if (props.aspectRatio) {
    config.aspectRatio = props.aspectRatio;
  }
  if (props.disableFlip !== undefined) {
    config.disableFlip = props.disableFlip;
  }

  return config;
};

const Html5QrcodePlugin = (props) => {
  const [isStopped, setIsStopped] = useState(true);
  const [showStopStartButton, setShowStopStartButton] = useState(false);
  const html5QrcodeScanner = useRef(null);
  const startButton = useRef(null);
  const html5StartButton = useRef(null);
  const html5StartButtonObserver = useRef(null);
  const html5StopButton = useRef(null);
  const html5StopButtonObserver = useRef(null);
  const html5RegionDashboard = useRef(null);
  const html5RegionDashboardObserver = useRef(null);
  const barcodeScannerRef = useRef(null);
  const barcodeScannerObserver = useRef(null);

  useEffect(() => {
    barcodeScannerObserver.current = new MutationObserver((mutations) => {
      const requestPermissionButton = document.getElementById(
        "html5-qrcode-button-camera-permission"
      );
      if (requestPermissionButton) {
        if (requestPermissionButton.className.indexOf("btn bnt-dark") === -1) {
          requestPermissionButton.className = `${requestPermissionButton.className} btn btn-dark`;
        }
      }

      const selects = document.querySelectorAll(
        `#${qrCodeRegionDashboardId} select`
      );
      for (let i = 0; i < selects.length; i++) {
        const select = selects[i];
        if (select.className.indexOf("form-select") === -1) {
          select.className = `${select.className} form-select`;
        }
      }

      if (!html5RegionDashboard.current) {
        html5RegionDashboard.current = document.getElementById(
          qrCodeRegionDashboardId
        );

        html5RegionDashboardObserver.current = new MutationObserver(
          (mutations) => {
            html5StartButton.current =
              document.getElementById(qrCodeStartButton);
            if (html5StartButton.current) {
              setShowStopStartButton(true);
              let displayStart = html5StartButton.current.style.display;
              setIsStopped(displayStart !== "none");

              html5StartButtonObserver.current = new MutationObserver(
                (mutations) => {
                  mutations.forEach((mutation) => {
                    if (mutation.type === "attributes") {
                      if (mutation.attributeName === "style") {
                        let newDisplay = html5StartButton.current.style.display;
                        if (displayStart != newDisplay) {
                          displayStart = newDisplay;
                          setIsStopped(newDisplay !== "none");
                        }
                      }
                    }
                  });
                }
              );

              html5StartButtonObserver.current.observe(
                html5StartButton.current,
                {
                  attributes: true,
                }
              );
            }

            html5StopButton.current = document.getElementById(qrCodeStopButton);
            if (html5StopButton.current) {
              setShowStopStartButton(true);
              let displayStop = html5StopButton.current.style.display;
              setIsStopped(displayStop === "none");

              html5StopButtonObserver.current = new MutationObserver(
                (mutations) => {
                  mutations.forEach((mutation) => {
                    if (mutation.type === "attributes") {
                      if (mutation.attributeName === "style") {
                        let newDisplay = html5StopButton.current.style.display;
                        if (displayStop != newDisplay) {
                          displayStop = newDisplay;
                          setIsStopped(newDisplay === "none");
                        }
                      }
                    }
                  });
                }
              );

              html5StopButtonObserver.current.observe(html5StopButton.current, {
                attributes: true,
              });
            }
          }
        );

        html5RegionDashboardObserver.current.observe(
          html5RegionDashboard.current,
          {
            attributes: false,
            childList: true,
            subtree: true,
          }
        );
      }
    });

    barcodeScannerObserver.current.observe(barcodeScannerRef.current, {
      attributes: false,
      childList: true,
      subtree: true,
    });

    // when component mounts
    const config = createConfig(props);
    const verbose = props.verbose === true;
    // Suceess callback is required.
    if (!props.qrCodeSuccessCallback) {
      throw "qrCodeSuccessCallback is required callback.";
    }
    html5QrcodeScanner.current = new Html5QrcodeScanner(
      qrcodeRegionId,
      config,
      verbose
    );
    html5QrcodeScanner.current.render((code) => {
      html5QrcodeScanner.current.clear();
      props.qrCodeSuccessCallback(code);
    }, props.qrCodeErrorCallback);

    // cleanup function when component will unmount
    return () => {
      html5QrcodeScanner.current.clear().catch((error) => {
        console.error("Failed to clear html5QrcodeScanner. ", error);
      });
    };
  }, []);

  return (
    <>
      <BarcodeScanner id={qrcodeRegionId} ref={barcodeScannerRef} />
      <div
        style={{
          position: "absolute",
          top: 5,
          right: 5,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {showStopStartButton && (
          <Button
            variant={"dark"}
            onClick={() => {
              if (isStopped) {
                html5StartButton.current.click();
                setIsStopped(false);
              } else {
                html5StopButton.current.click();
                setIsStopped(true);
              }
            }}
            ref={startButton}
          >
            {!isStopped && <Pause style={{ fontSize: 24 }} />}
            {isStopped && <Upc style={{ fontSize: 24 }} />}
          </Button>
        )}
        <Button
          variant={"dark"}
          onClick={() => {
            try {
              html5QrcodeScanner.current.html5Qrcode.stop();
              setTimeout(() => {
                html5QrcodeScanner.current.clear();
              }, 50);
            } catch (e) {
              console.error("Failed to stop scanner. ", e);
            }
            if (props.onStop) props.onStop();
          }}
        >
          <X style={{ fontSize: 24 }} />
        </Button>
      </div>
    </>
  );
};

export default Html5QrcodePlugin;
